import Card from '../../components/card/Card';
import { useCallback, useEffect, useState } from 'react';
import { downloadSignerDocument, deleteApplicationFile } from '../../services/ApplicationService';
import { ACCEPTED_FORMATS, MAX_FILE_SIZE } from '../../utils/Constants';
import { useParams } from 'react-router-dom';
import H from '../../components/texts/h/H';
import { clientData, signersData } from '../../types/interfaces';
import { useClientTheme } from '../../context/ThemeContext';
import {
  getMimeType,
  hasData,
  isSuccessfulResponse,
  removeFileExtension,
} from '../../utils/Utils';
import SummaryAmounts from '../common/SummaryAmounts';
import renderBasicData from '../common/BasicData';
import Icon from '../../components/icon/Icon';
import P from '../../components/texts/p/P';
import Button from '../../components/button/Button';
import Label from '../../components/texts/label/Label';
import Input from '../../components/input/Input';
import BulkDocumentUploader from '../../components/bulkDocumentUploader';
import { FileWithType } from '../../components/bulkDocumentUploader/BulkDocumentUploader.types';
import {
  FileRowContainer,
  DropdownWrapper,
  FileNameWrapper,
} from '../../components/bulkDocumentUploader/BulkDocumentUploader.styles';
import DropDown from '../../components/dropdown/DropDown';
import {
  downloadClientDocument,
  saveApplicationDocument,
  saveSignerDocument,
  downloadLufeCloudFile,
} from '../../services/ClientService';
import Signers from '../common/Signers';
import Span from '../../components/texts/span/Span';
import Spinner from '../../components/Spinner/spinner';
import { provinces } from '../../mocks/Provinces';
import JSZip from 'jszip';
import Tooltip from '../../components/tooltip/Tooltip';
import { reconcileDocuments } from '../../utils/documentMerge';
import { LocalDocument, getDocumentKey, normalizeFilename } from '../../utils/documentModel';
import { useRef } from 'react';
import { useToast } from '../../components/toast/toast';

type FileMetadata = LocalDocument;

interface ResumeProps {
  clientData: clientData;
  isSaving: boolean;
  onSave: Function;
  setClientData: (data: clientData) => void;
}

const Resume: React.FC<ResumeProps> = ({
  clientData,
  onSave,
  isSaving,
  setClientData,
}) => {
  const { clientTheme } = useClientTheme();
  const { cuit, id } = useParams();
  const [noteAmount, setNoteAmount] = useState<number>(clientData.note_amount || 0);
  const [guaranteeAmount, setGuaranteeAmount] = useState<number>(clientData.guarantee_amount || 0);
  const [extraFilesErrors, setExtraFilesErrors] = useState<string[]>([]);

  const [errors, setErrors] = useState<string[]>([]);
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const files = clientData.files;
  const allowedTypes = [
    '006',
    '011',
    '012',
    '013',
    '014',
    '015',
    '016',
    '017',
    '018',
    '019',
  ];
  const filteredFiles =
    files?.filter((file) => allowedTypes.includes(file.type) && !(file as any).isDeleted) || [];
  const [hasErrors, setHasErrors] = useState<boolean>(true);
const [hasFilesWithoutLabel, setHasFilesWithoutLabel] = useState<boolean>(false);
const [extraFiles, setExtraFiles] = useState<FileMetadata[]>([]);
  const [deletedKeys, setDeletedKeys] = useState<string[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const prevExtraFilesSignature = useRef<string>('');
  const isExtraFilesInitialized = useRef<boolean>(false);
  const isHydratingExtraFiles = useRef<boolean>(false);
  const skipNextExtraFilesChange = useRef<boolean>(false);
  const generateId = () =>
    `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  const signers =
    clientData.signers.length > 0
      ? clientData.signers
      : [];

  const getCleanFileName = (type: string, pathOrName: string): string => {
    const path = pathOrName || '';
    const filename = path.split('/').pop() || path;

    if (['015', '016', '017', '018', '019'].includes(type)) {
      let core = filename;

      const typeTag = `${type}-`;
      const typeIndex = core.indexOf(typeTag);
      if (typeIndex >= 0) {
        core = core.substring(typeIndex + typeTag.length);
      }

      core = core.replace(/^[0-9]{7,}-/, '');

      const lastDot = core.lastIndexOf('.');
      let namePart = core;
      let ext = '';
      if (lastDot > -1) {
        namePart = core.substring(0, lastDot);
        ext = core.substring(lastDot);
      }

      const tsMatch = namePart.match(/(.*)-\d{2}-\d{2}-\d{4}T\d{2}-\d{2}-\d{2}$/);
      if (tsMatch) {
        namePart = tsMatch[1];
      }

      return `${namePart}${ext}`;
    }

    return filename;
  };

  // Filtra documentos inválidos (sin tipo o sin nombre válido) solo cuando no hay archivo adjunto.
  // Permitimos temp-* o sin extensión si el File está presente (pendiente de subida).
  const isInvalidExtraFile = (f: Partial<LocalDocument>) => {
    if (!f.type) return true;
    const name = (f.asset_file_path || f.file_name || '').toString();
    const hasExt = name.includes('.');
    const isTemp = name.startsWith('temp-');
    const hasFile = Boolean((f as any).file);
    // Si trae File (pendiente), permitir aunque sea temp-* o sin extensión.
    if (hasFile) return false;
    // Si no hay File (viene de backend), exigir nombre válido.
    if (isTemp) return true;
    if (!hasExt) return true;
    return false;
  };

  useEffect(() => {
    setNoteAmount(clientData.note_amount || 0);
    setGuaranteeAmount(clientData.guarantee_amount || 0);
  }, [clientData.note_amount, clientData.guarantee_amount]);

  useEffect(() => {
    const dropdownItems = generateDataForDropdown(clientData.signers);
    isHydratingExtraFiles.current = true;

    const normalizeKey = (type?: string | null, name?: string | null) => {
      const base = (name || '').split('/').pop() || '';
      const withoutCuit = base.match(/^\d{11}-.+/) ? base.replace(/^\d{11}-/, '') : base;
      const timestampRegex = /(.*)-\d{2}-\d{2}-\d{4}T\d{2}-\d{2}-\d{2}(\.[^.]+)?$/;
      const withoutTs = base.match(timestampRegex) ? base.replace(timestampRegex, '$1$2' || '') : base;
      const withoutExt =
        withoutTs.includes('.') && withoutTs.lastIndexOf('.') > 0
          ? withoutTs.substring(0, withoutTs.lastIndexOf('.'))
          : withoutTs;
      return `${type || ''}::${withoutExt.toLowerCase()}`;
    };

    const dedupeGeneral = (
      docs: Array<{ file_type: string; asset_file_path?: string; file_name?: string; signer_cuit?: string; signer_type_code?: string }>
    ) => {
      const seen = new Map<string, any>();
      docs.forEach((d) => {
        const path = d.asset_file_path || d.file_name || '';
        const key = normalizeKey(d.file_type, path);
        const isTemp = path.startsWith('temp-');
        const existing = seen.get(key);
        if (!existing) {
          seen.set(key, d);
          return;
        }
        // Preferir no-temp sobre temp
        const existingIsTemp = (existing.asset_file_path || existing.file_name || '').startsWith('temp-');
        if (existingIsTemp && !isTemp) {
          seen.set(key, d);
        }
      });
      return Array.from(seen.values());
    };

    // Construir un Set de claves normalizadas de application_files para evitar duplicados con files
    const appFilesKeys = new Set<string>(
      (clientData.application_files || [])
        .filter((f: any) => ['013', '014', '015', '016', '017', '018', '019'].includes(f.file_type))
        .map((f: any) => {
          const path = f.asset_file_path || f.file_name || '';
          return normalizeKey(f.file_type, path);
        })
    );

    const baseFromBackend: LocalDocument[] = [
      ...dedupeGeneral(
        (clientData.application_files || []).filter((f: any) =>
          ['016', '017', '018', '019'].includes(f.file_type)
        )
      ).map((f: any, idx: number) => {
        const dropdownItem = findDropdownItemForDoc(
          { ...f, type: f.file_type },
          dropdownItems
        );
        const baseName = getCleanFileName(f.file_type, f.asset_file_path || f.file_name || '');
        const keyName = normalizeFilename(f.asset_file_path || f.file_name || baseName);
        const isDeleted = deletedKeys.some((k) => normalizeFilename(k) === keyName);
        return {
          id: f.asset_file_path || f.file_name || generateId(),
          type: f.file_type,
          label: dropdownItem?.label || '',
          asset_file_path: f.asset_file_path,
          file_name: baseName,
          isPending: false,
          isDeleted,
          order: idx,
        } as LocalDocument;
      }),
      ...(clientData.application_files || [])
        .filter((f: any) =>
          ['013', '014', '015'].includes(f.file_type)
        )
        .map((f: any, idx: number) => {
          const dropdownItem = findDropdownItemForDoc(
            { ...f, type: f.file_type },
            dropdownItems
          );
          const baseName = getCleanFileName(f.file_type, f.asset_file_path || f.file_name || '');
          const keyName = normalizeFilename(f.asset_file_path || f.file_name || baseName);
          const isDeleted = deletedKeys.some((k) => normalizeFilename(k) === keyName);
          return {
            id: f.asset_file_path || f.file_name || generateId(),
            type: f.file_type,
            label: dropdownItem?.label || '',
            asset_file_path: f.asset_file_path,
            file_name: baseName,
            isPending: false,
            isDeleted,
            order: idx,
          } as LocalDocument;
        }),
      ...(clientData.files || [])
        .filter(
          (f: any) =>
            f &&
            ['013', '014', '015', '016', '017', '018', '019'].includes(f.type)
        )
        .filter((f: any) => {
          const key = normalizeKey(f.type, f.file_name || '');
          return !appFilesKeys.has(key);
        })
        .map((f: any, idx: number) => {
          const dropdownItem = findDropdownItemForDoc(f, dropdownItems);
          const baseName = getCleanFileName(f.type, f.file_name || '');
          const keyName = normalizeFilename(f.file_name || baseName);
          const isDeleted = deletedKeys.some((k) => normalizeFilename(k) === keyName);
          return {
            id: f.file_name || generateId(),
            type: f.type,
            label: dropdownItem?.label || '',
            asset_file_path: f.file_name,
            file_name: baseName,
            isPending: false,
            isDeleted,
            order: idx + 1000,
          } as LocalDocument;
        }),
    ].filter((f) => !isInvalidExtraFile(f));

    console.log('[hydrate] baseFromBackend', baseFromBackend);

    setExtraFiles((prevExtraFiles) =>
      reconcileDocuments({
        base: baseFromBackend,
        locals: prevExtraFiles,
      })
    );
  }, [clientData.application_files, clientData.files, clientData.signers, deletedKeys]);

  useEffect(() => {
    const signature = JSON.stringify(
      extraFiles.map((f) => ({
        id: f.id,
        file_name: f.file_name,
        asset_file_path: f.asset_file_path,
        type: f.type,
        label: f.label,
        isPending: f.isPending,
        isDeleted: f.isDeleted,
        order: f.order,
      }))
    );

    if (!isExtraFilesInitialized.current) {
      prevExtraFilesSignature.current = signature;
      isExtraFilesInitialized.current = true;
      console.log('[extra-files] initial hydrate; signature stored');
      return;
    }

    if (isHydratingExtraFiles.current) {
      prevExtraFilesSignature.current = signature;
      isHydratingExtraFiles.current = false;
      console.log('[extra-files] hydrate sync; skip unsaved');
      return;
    }

    if (skipNextExtraFilesChange.current) {
      prevExtraFilesSignature.current = signature;
      skipNextExtraFilesChange.current = false;
      console.log('[extra-files] skipOnce sync; skip unsaved');
      return;
    }

    if (prevExtraFilesSignature.current !== signature) {
      setHasUnsavedChanges(true);
      prevExtraFilesSignature.current = signature;
      console.log('[extra-files] changed; setHasUnsavedChanges(true)');
    }
  }, [extraFiles]);

  useEffect(() => {
    console.log('[hasUnsavedChanges]', hasUnsavedChanges);
  }, [hasUnsavedChanges]);


  const renderSignersList = () => {
    return (
      <Card>
        <Card
          variant={clientTheme ? 'primary' : 'success'}
          modifier='weak'
          spacing={'2'}
          display='flex'
          justifyContent='space-between'
        >
          <H
            type='3'
            variant='neutral-1'
            withMargin={false}
            weight='bold'
            modifier='strong'
          >
            Firmantes
          </H>
        </Card>
        <Signers
          cuit={clientData.cuit}
          signersData={signers}
          isApplicationEditable={false}
          onChange={(e: { values: any[]; hasErrors: boolean }) => {
            setClientData({ ...clientData, signers: e.values });
            setHasErrors(e.hasErrors);
          }}
          applicationId={clientData.id_application?.toString() || id}
          requireValidations={true}
          allowEmptySigners={true}
        />
      </Card>
    );
  };

  const renderApplicationSummary = () => {
    return (
      <Card margin={{ top: '4' }}>
        <Card variant={clientTheme ? 'primary' : 'success'} modifier='weak' spacing={'2'}>
          <H
            type='3'
            variant='neutral-1'
            withMargin={false}
            weight='bold'
            modifier='strong'
          >
            Solicitud
          </H>
        </Card>
        <Card margin={{ top: '4' }}>
            <SummaryAmounts
              clientData={{
                ...clientData,
                note_amount: noteAmount,
                guarantee_amount: guaranteeAmount
              }}
              isManualAmount={clientData.approved_amount ? true : false}
              products={clientData.approved_products || []}
              amounts={clientData.approved_amount || []}
              editable={true}
              onNoteAmountChange={setNoteAmount}
              onGuaranteeAmountChange={setGuaranteeAmount}
            />
        </Card>
      </Card>
    );
  };

  const documents = [
    {
      id: '011',
      value: 'Estatuto',
    },
    {
      id: '012',
      value: 'Actas',
    },
    {
      id: '006',
      value: 'Certificado Pyme',
    },
  ];

  const downloadFileToBlob = async (
    type: string
  ): Promise<{ blob: Blob; filename: string } | null> => {
    try {
      const matchedFile = filteredFiles?.find((file) => file.type === type);
      if (!matchedFile) {
        return null;
      }

      const filename = matchedFile.file_name;
      if (!filename) {
        return null;
      }

      let response;

      if (filename.includes('.')) {
        response = await downloadClientDocument(cuit || '', filename);

        if (!response.success && response.status === 500) {
          response = await downloadLufeCloudFile(
            cuit || '',
            filename.replace(/\.[^/.]+$/, '')
          );
        }

        if (response && response.success && response.data) {
          const mimeType = getMimeType(filename);

          if (response.data instanceof Blob) {
            return {
              blob: response.data,
              filename: filename,
            };
          } else {
            return {
              blob: new Blob([response.data], { type: mimeType }),
              filename: filename,
            };
          }
        }
      } else {
        response = await downloadLufeCloudFile(cuit || '', filename);
        if (response && response.success && response.data) {
          if (response.data instanceof Blob) {
            return {
              blob: response.data,
              filename: `${filename}.pdf`,
            };
          } else {
            return {
              blob: new Blob([response.data], { type: 'application/pdf' }),
              filename: `${filename}.pdf`,
            };
          }
        }
      }

      return null;
    } catch (error) {
      console.error(`Error downloading file to blob (type: ${type}):`, error);
      return null;
    }
  };

  const handleDownloadAll = async () => {
    const hasDocuments = filteredFiles && filteredFiles.length > 0;
    if (!hasDocuments) {
      return;
    }

    setBulkDownloading(true);

    try {
      const zip = new JSZip();
      let successCount = 0;

      for (const file of filteredFiles) {
        try {
          const fileData = await downloadFileToBlob(file.type);

          if (fileData) {
            zip.file(fileData.filename, fileData.blob);
            successCount++;
          }
        } catch (fileError) {
          console.error('Error downloading file:', fileError);
        }

        await new Promise((resolve) => setTimeout(resolve, 300));
      }

      if (successCount === 0) {
        alert('No se pudo descargar ningún archivo. Por favor, inténtalo de nuevo.');
        return;
      }

      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hour = String(now.getHours()).padStart(2, '0');
      const minute = String(now.getMinutes()).padStart(2, '0');
      const timestamp = `${year}${month}${day}-${hour}${minute}`;
      const sanitizedCompanyName = (clientData.basic_data?.company_name && clientData.basic_data.company_name.trim())
        ? clientData.basic_data.company_name.replace(/[^a-zA-Z0-9\s]/g, '_').replace(/\s+/g, '_')
        : cuit || 'empresa';
      const zipFilename = `${sanitizedCompanyName}-instrumentacion-${timestamp}.zip`;

      const zipBlob = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 },
      });

      const url = window.URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = zipFilename;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error('Error creating ZIP:', error);
      alert('Error al crear el archivo ZIP. Por favor, inténtalo de nuevo.');
    } finally {
      setBulkDownloading(false);
    }
  };

  const renderDocuments = () => {
    return documents.map((document) => {
      const matchedFile = filteredFiles?.find(
        (file) => file.type === document.id
      );

      return (
        <Card
          key={document.id}
          margin={{ top: '5', bottom: '5' }}
          spacing={'3'}
        >
          <Card
            margin={{ bottom: '3' }}
            display='flex'
            alignItems='center'
            gap='16px'
            justifyContent='space-between'
          >
            <Card display='flex' gap='16px'>
              <Icon variant='neutral' name='document_outline' />
              <Card>
                <P
                  type='3'
                  withMargin={false}
                  variant='neutral'
                  modifier='strong'
                >
                  {document.value}
                </P>
              </Card>
            </Card>

            <Card>
              <Card display='flex' alignItems='center' gap='10px'>
                {matchedFile ? (
                  <>
                    <Card
                      width='300px'
                      variant='neutral'
                      modifier='inverted'
                      height='46px'
                      spacing={{ left: '1', right: '1' }}
                      borderRadiusAmount='3'
                      border
                      display='flex'
                      alignItems='center'
                    >
                      <Label variant='neutral' modifier='strong' type='2'>
                        {matchedFile.file_name.includes('.')
                          ? matchedFile.file_name
                          : 'Documento asignado desde LUFE'}
                      </Label>
                    </Card>

                    <Button
                      variant='secondary'
                      size='md'
                      onClick={() => handleDownloadFile(matchedFile.type)}
                    >
                      <Icon
                        name='view_outline'
                        variant='neutral'
                        height={44}
                        cursor='pointer'
                      />
                    </Button>
                    <Button
                      variant='secondary'
                      size='md'
                      onClick={() => handleFileDelete(matchedFile.type)}
                    >
                      <Icon
                        name='delete_outline'
                        variant='neutral'
                        height={44}
                        cursor='pointer'
                      />
                    </Button>
                  </>
                ) : (
                  <Card
                    display='flex'
                    justifyContent='space-between'
                    gap='10px'
                    width='444px'
                  >
                    <Input
                      id={`file-input-${document.id}`}
                      type='file'
                      size='md'
                      icons={{ iconRight: 'upload_outline' }}
                      onChange={(event) => handleFileChange(event, document.id)}
                      accept='.pdf, .jpg, .jpeg, .png, .xls, .xlsx, .doc, .docx'
                    />
                  </Card>
                )}
              </Card>
            </Card>
          </Card>
        </Card>
      );
    });
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    documentId: string
  ) => {
    const file = event.target.files?.[0] || null;
    let errorMessage: string = '';
    const newErrors = [...errors];

    if (file) {
      if (!ACCEPTED_FORMATS.includes(file.type)) {
        errorMessage =
          'El formato de archivo solo puede ser: .pdf, .jpg, .jpeg, .png, .xls, .xlsx, .doc, .docx';
      } else if (file.size > MAX_FILE_SIZE) {
        errorMessage = 'El tamaño del archivo no puede ser mayor de 20 MB';
      }

      if (errorMessage) {
        const inputElement = document.getElementById(
          `file-input-${documentId}`
        ) as HTMLInputElement;
        if (inputElement) {
          inputElement.value = '';
        }
        useToast.error({
          title: errorMessage,
          id: 'application-toast',
          description: '',
          closeButton: true,
          autoClose: 5000,
        });
        newErrors.push(errorMessage);
        setErrors(newErrors);
        return;
      }

      const fileName = removeFileExtension(file.name);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', documentId);
      formData.append('fileName', fileName);
      formData.append('isBasicData', 'true');

      if (!id) {
        console.error('Application ID is required');
        return;
      }

      saveApplicationDocument(cuit, id, formData).then((response) => {
        if (hasData(response) && isSuccessfulResponse(response)) {
          const newFiles = [...(files || [])];
          const fileToSend = {
            file: file.name,
            file_name: response.data,
            type: documentId,
            is_basic_data: true,
          };

          const fileIndex = newFiles.findIndex(
            (item) => item?.type === documentId
          );

          if (fileIndex === -1) {
            newFiles.push(fileToSend);
          } else {
            newFiles[fileIndex] = fileToSend;
          }

          setClientData({
            ...clientData,
            files: newFiles,
          });

          newErrors.push('');
          setErrors(newErrors);
          setHasUnsavedChanges(true);
        } else {
          let errorMsg = 'Error al subir el archivo';
          
          if (response.status === 413) {
            errorMsg = 'El archivo es demasiado grande. Por favor, intente con un archivo más pequeño.';
          } else if (response.message && response.status !== 500) {
            errorMsg = response.message;
          }
          
          console.error('Error uploading file:', response);
          useToast.error({
            title: errorMsg,
            id: 'application-toast',
            description: '',
            closeButton: true,
            autoClose: 5000,
          });
          newErrors.push(errorMsg);
          setErrors(newErrors);
        }
      }).catch((error) => {
        console.error('Error uploading file:', error);
        const errorMsg = 'Error al subir el archivo';
        useToast.error({
          title: errorMsg,
          id: 'application-toast',
          description: '',
          closeButton: true,
          autoClose: 5000,
        });
        newErrors.push(errorMsg);
        setErrors(newErrors);
      });
    }
  };

  const generateDataForDropdown = (
    signers?: { name: string; cuit: string; signer_type_code: string[] }[]
  ) => {
    const dropdownItems: Array<{
      id: string;
      label: string;
      cuit: string;
      signer_type_code: string;
      isGeneral: boolean;
    }> = [];

    // Add signer-specific documents first if signers exist
    if (signers && signers.length > 0) {
      const validSigners = signers.filter(
        (signer) => signer.name && signer.name.trim() !== ''
      );

      if (validSigners.length > 0) {
        const signerDocuments = validSigners.flatMap((signer) => [
          {
            id: `013`,
            label: `DNI frente de ${signer.name}`,
            cuit: signer.cuit,
            signer_type_code: signer.signer_type_code[0],
            isGeneral: false,
          },
          {
            id: `014`,
            label: `DNI dorso de ${signer.name}`,
            cuit: signer.cuit,
            signer_type_code: signer.signer_type_code[0],
            isGeneral: false,
          },
          {
            id: `015`,
            label: `Otros de ${signer.name}`,
            cuit: signer.cuit,
            signer_type_code: signer.signer_type_code[0],
            isGeneral: false,
          },
        ]);

        dropdownItems.push(...signerDocuments);
      }
    }

    const generalDocuments = [
      {
        id: `016`,
        label: `Contragarantías`,
        cuit: '',
        signer_type_code: '',
        isGeneral: true,
      },
      {
        id: `017`,
        label: `Balance`,
        cuit: '',
        signer_type_code: '',
        isGeneral: true,
      },
      {
        id: `018`,
        label: `Poderes`,
        cuit: '',
        signer_type_code: '',
        isGeneral: true,
      },
      {
        id: `019`,
        label: `Otros`,
        cuit: '',
        signer_type_code: '',
        isGeneral: true,
      },
    ];

    dropdownItems.push(...generalDocuments);

    return dropdownItems;
  };

  const isSignerDocumentType = (type?: string | null) =>
    type ? ['013', '014', '015'].includes(type) : false;

  const extractSignerInfoFromFilename = (path?: string | null) => {
    if (!path) return { signer_cuit: null as string | null, signer_type_code: null as string | null };
    const filename = path.split('/').pop() || path;
    const parts = filename.split('-');
    const cuits: string[] = [];
    let signerTypeCode: string | null = null;

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (part && /^\d{11}$/.test(part)) {
        cuits.push(part);
        const next = parts[i + 1];
        if (next && /^\d{3}$/.test(next)) {
          signerTypeCode = next;
        }
      }
    }

    const signer_cuit = cuits.length >= 2 ? cuits[1] : cuits[0] || null;
    return { signer_cuit, signer_type_code: signerTypeCode };
  };

  const findDropdownItemForDoc = (
    doc: Partial<LocalDocument> & {
      file_type?: string;
      signer_cuit?: string;
      signer_type_code?: string;
      signerTypeCode?: string;
      signerCuit?: string;
    },
    dropdownItems: ReturnType<typeof generateDataForDropdown>
  ) => {
    const type = doc.type || doc.file_type;
    if (!type) return null;

    if (!isSignerDocumentType(type)) {
      return (
        dropdownItems.find((item) => item.id === type && item.isGeneral) ||
        dropdownItems.find((item) => item.id === type) ||
        null
      );
    }

    const signer_cuit =
      doc.signer_cuit ||
      (doc as any).signerCuit ||
      extractSignerInfoFromFilename(doc.asset_file_path || doc.file_name).signer_cuit;
    const signer_type_code =
      doc.signer_type_code ||
      doc.signerTypeCode ||
      extractSignerInfoFromFilename(doc.asset_file_path || doc.file_name).signer_type_code;

    if (signer_cuit && signer_type_code) {
      const match = dropdownItems.find(
        (item) =>
          item.id === type &&
          !item.isGeneral &&
          item.cuit === signer_cuit &&
          item.signer_type_code === signer_type_code
      );
      if (match) return match;
    }

    if (doc.label) {
      const byLabel = dropdownItems.find(
        (item) => item.id === type && item.label === doc.label
      );
      if (byLabel) return byLabel;
    }

    return dropdownItems.find((item) => item.id === type) || null;
  };

  // Convert generateDataForDropdown result to DocumentType[] for BulkDocumentUploader
  const getDocumentTypesForBulkUploader = () => {
    const dropdownItems = generateDataForDropdown(clientData.signers);
    return dropdownItems.map((item) => ({
      id: item.id,
      label: item.label,
      disabled: false,
    }));
  };

  const handleBulkValidationChange = useCallback(
    (hasFilesWithoutType: boolean) => {
      requestAnimationFrame(() => setHasFilesWithoutLabel(hasFilesWithoutType));
    },
    []
  );

  const handleBulkFileUpload = async (
    file: File,
    type: string,
    _label: string
  ): Promise<{ success: boolean; data?: string; message?: string }> => {
    const fileName = removeFileExtension(file.name);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const tempFileName = `temp-${type}-${timestamp}-${fileName}`;
    
    return {
      success: true,
      data: tempFileName,
    };
  };

  const handleBulkFilesUploaded = (uploadedFiles: FileWithType[]) => {
    let processed = false;
    const dropdownItems = generateDataForDropdown(clientData.signers);

    console.log('[bulk-upload] received', uploadedFiles.map((f) => ({
      id: f.id,
      type: f.type,
      label: f.label,
      status: f.uploadStatus,
      uploadedFileName: f.uploadedFileName,
    })));

    uploadedFiles.forEach((uploadedFile) => {
      if (!uploadedFile.type || uploadedFile.uploadStatus !== 'success') {
        return;
      }

      let dropdownItem = dropdownItems.find(
        (item) =>
          item.id === uploadedFile.type && item.label === uploadedFile.label
      );
      
      if (!dropdownItem && uploadedFile.uploadedFileName) {
        const filename = uploadedFile.uploadedFileName;
        const parts = filename.split('-');
        const cuits: string[] = [];
        let signerTypeCode: string | null = null;
        
        for (let i = 0; i < parts.length; i++) {
          const part = parts[i];
          if (part && /^\d{11}$/.test(part)) {
            cuits.push(part);
            // El signer_type_code suele estar después del segundo CUIT
            if (cuits.length === 2 && i + 1 < parts.length && parts[i + 1] && /^\d{3}$/.test(parts[i + 1])) {
              signerTypeCode = parts[i + 1];
              break;
            }
          }
        }
        
        // Si encontramos CUIT y signer_type_code, buscar el dropdownItem exacto
        if (cuits.length >= 2 && signerTypeCode) {
          const firmanteCuit = cuits[1]; // Segundo CUIT es el del firmante
          dropdownItem = dropdownItems.find(
            (item) =>
              item.id === uploadedFile.type &&
              !item.isGeneral &&
              item.cuit === firmanteCuit &&
              item.signer_type_code === signerTypeCode
          );
        } else if (cuits.length >= 2) {
          const firmanteCuit = cuits[1];
          dropdownItem = dropdownItems.find(
            (item) =>
              item.id === uploadedFile.type &&
              !item.isGeneral &&
              item.cuit === firmanteCuit
          );
        }
      }
      
      if (!dropdownItem) {
        dropdownItem = dropdownItems.find((item) => item.id === uploadedFile.type);
      }
      
      if (!dropdownItem) {
        return;
      }

      if (dropdownItem.isGeneral) {
        // No actualizar clientData.files aquí, se hará en handleSave con el nombre final
        const assetName = uploadedFile.uploadedFileName || uploadedFile.fileName;
        
        setExtraFiles((prev) => {
          const docId = assetName || uploadedFile.id;
          const next = [...prev];
          const existingIndex = next.findIndex(
            (f) =>
              f.id === docId ||
              f.asset_file_path === assetName ||
              f.file_name === uploadedFile.fileName ||
              f.id === uploadedFile.id
          );
          const order =
            existingIndex === -1
              ? next.length
              : next[existingIndex].order ?? existingIndex;
          const newExtraFile: FileMetadata = {
            id: docId,
            file_name: uploadedFile.fileName,
            type: dropdownItem.id,
            asset_file_path: assetName || '',
            label: dropdownItem.label,
            file: uploadedFile.file,
            order,
          };

          if (existingIndex === -1) {
            next.push(newExtraFile);
          } else {
            next[existingIndex] = {
              ...next[existingIndex],
              ...newExtraFile,
            };
          }

          return next;
        });

        // No actualizar clientData.files aquí
        processed = true;
      } else {
        const assetPath =
          uploadedFile.uploadedFileName || uploadedFile.fileName;

        const existingIndex = extraFiles.findIndex(
          (f) => f.id === uploadedFile.id
        );
        const order =
          existingIndex === -1
            ? extraFiles.length
            : extraFiles[existingIndex].order ?? existingIndex;
        const newExtraFile: FileMetadata = {
          id: uploadedFile.id,
          file_name: getCleanFileName(dropdownItem.id, assetPath),
          type: dropdownItem.id,
          asset_file_path: assetPath,
          label: dropdownItem.label,
          file: uploadedFile.file,
          order,
        };

        const updatedExtraFiles = (() => {
          if (existingIndex === -1) {
            return [...extraFiles, newExtraFile];
          }
          const clone = [...extraFiles];
          clone[existingIndex] = newExtraFile;
          return clone;
        })();
        setExtraFiles(updatedExtraFiles);
        processed = true;
      }
    });

    if (processed) {
      setHasUnsavedChanges(true);
      setHasFilesWithoutLabel(false);
      console.log('[bulk-upload] processed => setHasUnsavedChanges(true)');
    }
  };

  const handleDropdownChange = (label: string, fileId: string) => {
    const dropdownItems = generateDataForDropdown(clientData.signers);
    const dropdownItem = dropdownItems.find((item) => item.label === label);
    if (!dropdownItem) return;

    const updatedFiles = [...extraFiles];
    const updatedErrors = [...extraFilesErrors];

    const index = updatedFiles.findIndex((f) => f.id === fileId);
    if (index === -1) return;
    const file = updatedFiles[index];

    console.log('[dropdown-change]', {
      fileId,
      prevType: file.type,
      prevLabel: file.label,
      nextType: dropdownItem.id,
      nextLabel: dropdownItem.label,
    });

    updatedFiles[index] = {
      ...file,
      type: dropdownItem.id,
      label: dropdownItem.label,
    };
    
    if (file.asset_file_path && dropdownItem.cuit && dropdownItem.signer_type_code) {
      try {
        const labelKey = `file_label_${file.asset_file_path}`;
        const signerKey = `file_signer_${file.asset_file_path}`;
        
        // También guardar con el filename (sin path completo) para archivos re-subidos
        const filename = file.asset_file_path.split('/').pop() || file.asset_file_path;
        const filenameLabelKey = `file_label_${filename}`;
        const filenameSignerKey = `file_signer_${filename}`;
        
        localStorage.setItem(labelKey, dropdownItem.label);
        localStorage.setItem(signerKey, JSON.stringify({
          cuit: dropdownItem.cuit,
          signer_type_code: dropdownItem.signer_type_code
        }));
        
        if (filename !== file.asset_file_path) {
          localStorage.setItem(filenameLabelKey, dropdownItem.label);
          localStorage.setItem(filenameSignerKey, JSON.stringify({
            cuit: dropdownItem.cuit,
            signer_type_code: dropdownItem.signer_type_code
          }));
        }
        
      } catch (e) {
        console.warn('No se pudo guardar en localStorage al cambiar etiqueta:', e);
      }
    }
    
    updatedErrors[index] = '';
    
    setExtraFiles(updatedFiles);
    setExtraFilesErrors(updatedErrors);
    setHasUnsavedChanges(true);
  };

  const handleFileDelete = (documentId: string) => {
    const fileIndex = filteredFiles.findIndex(
      (item) => item?.type === documentId
    );
    const filename = filteredFiles.find(
      (item) => item?.type === documentId
    )?.file_name;

    if (filename && fileIndex !== -1 && id) {
      const encoded = encodeURIComponent(filename);
      deleteApplicationFile(cuit, id, encoded, documentId).then((response) => {
        if (response.success) {
          const updatedFiles = [...files];
          updatedFiles.splice(fileIndex, 1);
          setClientData({
            ...clientData,
            files: updatedFiles,
          });

          const updatedErrors = [...errors];
          updatedErrors.splice(fileIndex, 1);
          setErrors(updatedErrors);
          setHasUnsavedChanges(true);
        }
      });
    }
  };

  const handleDownloadFile = (documentId: string) => {
    const fileObj = filteredFiles.find((item) => item?.type === documentId);
    const filename = fileObj?.file_name;
    if (!filename) return;

    if (filename.includes('.')) {
      downloadClientDocument(cuit, filename).then((response) => {
        if (response.success) {
          const mimeType = getMimeType(filename);
          const fileBlob = new Blob([response.data], { type: mimeType });
          const url = window.URL.createObjectURL(fileBlob);

          if (
            mimeType.startsWith('application/pdf') ||
            mimeType.startsWith('image/')
          ) {
            window.open(url, '_blank');
          } else {
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            window.URL.revokeObjectURL(url);
          }
        }
      });
    }
  };

  const handleExtraFileDownload = async (assetFilePath: string, fileType?: string) => {
    const filename = assetFilePath?.split('/').pop();
    if (!filename || !cuit) return;
    
    // Determine which endpoint to use based on file type
    const isGeneralDocument = fileType && ['016', '017', '018', '019'].includes(fileType);
    
    let response;
    if (isGeneralDocument) {
      // For general documents, use downloadClientDocument
      response = await downloadClientDocument(cuit, filename);
    } else {
      // For signer documents, use downloadSignerDocument
      if (!id) return;
      response = await downloadSignerDocument(id, cuit, filename);
    }
    
    if (response.success) {
      const mimeType = getMimeType(filename);
      const fileBlob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(fileBlob);
      if (
        mimeType.startsWith('application/pdf') ||
        mimeType.startsWith('image/')
      ) {
        window.open(url, '_blank');
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    }
  };

  const handleExtraFileDelete = async (file: any, fileId: string) => {
    const filenameBase = file.asset_file_path?.split('/').pop() || file.file_name || '';
    const rawAsset = file.asset_file_path || '';
    if (!filenameBase || !cuit || !id) return;
    const normalizedKey = normalizeFilename(filenameBase);
    const isGeneralDocument = file.type && ['016', '017', '018', '019'].includes(file.type);

    const deleteCandidates = [rawAsset, filenameBase].filter(Boolean);
    for (const candidate of deleteCandidates) {
      try {
        const encoded = encodeURIComponent(candidate);
        const response = await deleteApplicationFile(cuit, id, encoded, file.type);
        if (response?.success) {
          break;
        }
      } catch (e: any) {
        if (e?.response?.status === 404 || e?.response?.status === 500) {
          break;
        }
        console.error('[extra-delete] error al borrar backend', e);
        return;
      }
    }

    const deletedKey = normalizeFilename(rawAsset || filenameBase);
    setDeletedKeys((prev) => {
      const next = prev.includes(deletedKey) ? prev : [...prev, deletedKey];
      return next;
    });

    const newExtraFiles = extraFiles.filter((f) => f.id !== fileId);
    setExtraFiles(newExtraFiles);
    setExtraFilesErrors((prev) =>
      prev.filter((_, i) => newExtraFiles[i]?.id !== undefined)
    );

    if (isGeneralDocument) {
      const updatedFiles = [...(files || [])];
      const fileIndex = updatedFiles.findIndex(
        (item) =>
          item?.type === file.type ||
          item?.file_name === file.asset_file_path ||
          item?.file_name === file.file_name
      );
      if (fileIndex !== -1) {
        updatedFiles.splice(fileIndex, 1);
      }

      const updatedAppFiles = (clientData.application_files || []).filter(
        (appFile) => {
          const appName = appFile.asset_file_path || '';
          const base = appName.split('/').pop() || appName;
          return (
            appFile.asset_file_path !== file.asset_file_path &&
            appFile.asset_file_path !== file.file_name &&
            base !== filenameBase &&
            !base.includes(filenameBase)
          );
        }
      );

      setClientData({
        ...clientData,
        files: updatedFiles,
        application_files: updatedAppFiles,
      });
    } else {
      const updatedAppFiles = (clientData.application_files || []).filter(
        (appFile) => {
          const appName = appFile.asset_file_path || '';
          const base = appName.split('/').pop() || appName;
          return (
            appFile.asset_file_path !== file.asset_file_path &&
            appFile.asset_file_path !== file.file_name &&
            base !== filenameBase &&
            !base.includes(filenameBase)
          );
        }
      );
      setClientData({
        ...clientData,
        application_files: updatedAppFiles,
      });
    }

    setHasUnsavedChanges(true);
  };

  const uploadLocalDocument = async (
    doc: LocalDocument,
    dropdownItems: ReturnType<typeof generateDataForDropdown>
  ): Promise<LocalDocument> => {
    const isTemp = doc.asset_file_path?.startsWith('temp-');
    const hasFile = !!doc.file;
    if (!hasFile && !isTemp) return doc;

    const formData = new FormData();
    if (doc.file) {
      formData.append('file', doc.file);
    }
    formData.append('type', doc.type);

    const baseName = removeFileExtension(
      (doc.file && doc.file.name) || doc.file_name || 'documento'
    );
    const fileName = `${doc.type}-${baseName}`;
    formData.append('fileName', fileName);
    formData.append('isBasicData', 'true');

    const isGeneral = ['016', '017', '018', '019'].includes(doc.type);
    let response;
    if (isGeneral) {
      if (!id) {
        throw new Error('Application ID is required');
      }
      response = await saveApplicationDocument(cuit, id, formData);
    } else {
      const di = dropdownItems.find(
        (d) => d.id === doc.type && !d.isGeneral && d.label === doc.label
      );
      if (!di) return doc;
      response = await saveSignerDocument(di.cuit, formData);
    }

    if (hasData(response) && isSuccessfulResponse(response)) {
      return {
        ...doc,
        asset_file_path: response.data,
        file_name: response.data,
        isPending: false,
      };
    } else {
      let errorMsg = 'Error al subir el archivo';
      
      if (response.status === 413) {
        errorMsg = 'El archivo es demasiado grande. Por favor, intente con un archivo más pequeño.';
      } else if (response.message && response.status !== 500) {
        errorMsg = response.message;
      }
      
      console.error('Error uploading local document:', response);
      useToast.error({
        title: errorMsg,
        id: 'application-toast',
        description: '',
        closeButton: true,
        autoClose: 5000,
      });
    }
    return doc;
  };

  const handleSave = async (shouldContinue: boolean = true) => {
    if (!cuit) return;

    const dropdownItems = generateDataForDropdown(clientData.signers);

    const normalizeKey = (type?: string | null, name?: string | null) => {
      const base = (name || '').split('/').pop() || '';
      const withoutCuit = base.match(/^\d{11}-.+/) ? base.replace(/^\d{11}-/, '') : base;
      const timestampRegex = /(.*)-\d{2}-\d{2}-\d{4}T\d{2}-\d{2}-\d{2}(\.[^.]+)?$/;
      const withoutTs = base.match(timestampRegex) ? base.replace(timestampRegex, '$1$2' || '') : base;
      const withoutExt =
        withoutTs.includes('.') && withoutTs.lastIndexOf('.') > 0
          ? withoutTs.substring(0, withoutTs.lastIndexOf('.'))
          : withoutTs;
      return `${type || ''}::${withoutExt.toLowerCase()}`;
    };

    const isDeletedKey = (type: string | undefined, name: string | undefined) => {
      if (!name) return false;
      const key = normalizeKey(type, name);
      const raw = normalizeFilename(name);
      return deletedKeys.some((k) => k === raw || normalizeKey(type, k) === key);
    };

    const filesToUpload = extraFiles.filter((f) => {
      if (!f.type || !f.label || !(f.file instanceof File)) {
        return false;
      }
      
      if (f.asset_file_path && f.asset_file_path.startsWith('temp-')) {
        return true;
      }
      
      if (!f.asset_file_path) {
        return true;
      }
      
      const dropdownItem = dropdownItems.find((item) => item.id === f.type && item.label === f.label);
      if (!dropdownItem || dropdownItem.isGeneral) {
        return false;
      }
      const filename = f.asset_file_path.split('/').pop() || f.asset_file_path;
      const parts = filename.split('-');
      const cuitsInFilename: string[] = [];
      for (const part of parts) {
        if (part && /^\d{11}$/.test(part)) {
          cuitsInFilename.push(part);
        }
      }
      
      if (cuitsInFilename.length >= 2 && cuitsInFilename[1] !== dropdownItem.cuit) {
        return true;
      }
      
      return false;
    });

    const updatedExtraFiles = [...extraFiles];
    for (let i = 0; i < filesToUpload.length; i++) {
      const file = filesToUpload[i];
      const fileIndex = extraFiles.findIndex((f) => f.id === file.id);
      if (fileIndex === -1) {
        continue;
      }

      if (!file.file || !(file.file instanceof File)) {
        continue;
      }

      const dropdownItem = dropdownItems.find((item) => item.id === file.type && item.label === file.label);
      if (!dropdownItem) {
        continue;
      }

      const formData = new FormData();
      formData.append('file', file.file);
      formData.append('type', dropdownItem.id);

      // Usar siempre el nombre limpio del archivo, sin CUITs ni códigos previos
      let fileNameToUse;
      if (file.file) {
        // Si hay un File object, usar su nombre original limpio
        fileNameToUse = removeFileExtension(file.file.name);
      } else {
        // Si no hay File (ya está subido), obtener el nombre limpio del path/filename
        fileNameToUse = getCleanFileName(file.type, file.file_name);
      }

      let fileName;
      if (dropdownItem.isGeneral) {
        fileName = `${dropdownItem.id}-${fileNameToUse}`;
        formData.append('fileName', fileName);
        formData.append('isBasicData', 'true');
      } else {
        // Solo enviar el código y el nombre, el backend agrega el CUIT automáticamente
        fileName = `${dropdownItem.signer_type_code}-${fileNameToUse}`;
        formData.append('fileName', fileName);
      }

      try {
        let response;
        if (dropdownItem.isGeneral) {
          if (!id) {
            throw new Error('Application ID is required');
          }
          response = await saveApplicationDocument(cuit, id, formData);
        } else {
          response = await saveSignerDocument(dropdownItem.cuit, formData);
        }

        if (hasData(response) && isSuccessfulResponse(response)) {
          
          if (file.asset_file_path && file.asset_file_path !== response.data) {
          }
          
          if (response.data && file.label && dropdownItem.cuit && dropdownItem.signer_type_code) {
            const labelKey = `file_label_${response.data}`;
            const signerKey = `file_signer_${response.data}`;
            
            const filename = response.data.split('/').pop() || response.data;
            const filenameLabelKey = `file_label_${filename}`;
            const filenameSignerKey = `file_signer_${filename}`;
            
            let originalFilenameKey: string | null = null;
            let originalSignerKey: string | null = null;
            if (file.asset_file_path && file.asset_file_path !== response.data) {
              const originalFilename = file.asset_file_path.split('/').pop() || file.asset_file_path;
              originalFilenameKey = `file_label_${originalFilename}`;
              originalSignerKey = `file_signer_${originalFilename}`;
            }
            
            try {
              localStorage.setItem(labelKey, file.label);
              localStorage.setItem(signerKey, JSON.stringify({
                cuit: dropdownItem.cuit,
                signer_type_code: dropdownItem.signer_type_code
              }));
              
              if (filename !== response.data) {
                localStorage.setItem(filenameLabelKey, file.label);
                localStorage.setItem(filenameSignerKey, JSON.stringify({
                  cuit: dropdownItem.cuit,
                  signer_type_code: dropdownItem.signer_type_code
                }));
              }
              
              if (originalFilenameKey && originalSignerKey) {
                localStorage.setItem(originalFilenameKey, file.label);
                localStorage.setItem(originalSignerKey, JSON.stringify({
                  cuit: dropdownItem.cuit,
                  signer_type_code: dropdownItem.signer_type_code
                }));
              }
            } catch (e) {
              console.warn('No se pudo guardar en localStorage:', e);
            }
          }
          
          updatedExtraFiles[fileIndex] = {
            ...file,
            asset_file_path: response.data,
            file_name: response.data,
            label: file.label,
            type: file.type,
          };
          if (dropdownItem.isGeneral && file.file) {
            const newFiles = [...(files || [])];
            const fileToSend = {
              file: file.file.name,
              file_name: response.data,
              type: dropdownItem.id,
              is_basic_data: true,
            };

            const existingFileIndex = newFiles.findIndex(
              (item) => item?.type === dropdownItem.id
            );

            if (existingFileIndex === -1) {
              newFiles.push(fileToSend);
            } else {
              newFiles[existingFileIndex] = fileToSend;
            }

            setClientData({
              ...clientData,
              files: newFiles,
            });
          }
        } else {
          let errorMsg = 'Error al subir el archivo';
          
          if (response.status === 413) {
            errorMsg = 'El archivo es demasiado grande. Por favor, intente con un archivo más pequeño.';
          } else if (response.message && response.status !== 500) {
            errorMsg = response.message;
          }
          
          console.error(`❌ Error al subir archivo ${file.file_name}:`, response);
          useToast.error({
            title: errorMsg,
            id: 'application-toast',
            description: '',
            closeButton: true,
            autoClose: 5000,
          });
        }
      } catch (error) {
        console.error(`❌ Error al subir archivo ${file.file_name}:`, error);
        useToast.error({
          title: `Error al subir el archivo ${file.file_name}`,
          id: 'application-toast',
          description: '',
          closeButton: true,
          autoClose: 5000,
        });
      }
    }


    setExtraFiles(updatedExtraFiles);

    // 1. Preservar TODOS los archivos de application_files (sin filtrar por tipo)
    const filesFromApplicationFiles = (clientData.application_files || [])
      .filter((f) => 
        !isDeletedKey(f.file_type, f.asset_file_path)
      )
      .map((f) => ({
        file_type: f.file_type,
        asset_file_path: f.asset_file_path,
        ...(f.signer_cuit && { signer_cuit: f.signer_cuit }),
        ...(f.signer_type_code && { signer_type_code: f.signer_type_code }),
      }));

    // 2. Crear set de archivos ya incluidos para evitar duplicados
    const includedKeys = new Set(
      filesFromApplicationFiles.map((f) => 
        normalizeKey(f.file_type, f.asset_file_path)
      )
    );

    // 3. Agregar archivos nuevos desde files (sin filtrar por tipo)
    const filesFromFiles = (clientData.files || [])
      .filter((f) => {
        if ((f as any).isDeleted) return false;
        if (isInvalidExtraFile(f as any)) return false;
        if (isDeletedKey(f.type, f.file_name)) return false;
        // Evitar duplicados con application_files
        const key = normalizeKey(f.type, f.file_name);
        if (includedKeys.has(key)) return false;
        return true;
      })
      .map((f) => ({
        file_type: f.type,
        asset_file_path: f.file_name,
      }));

    // 4. Combinar ambas fuentes
    const baseFiles = [...filesFromApplicationFiles, ...filesFromFiles];

    const extra = updatedExtraFiles
      .filter(
        (f) =>
          f.type &&
          f.asset_file_path &&
          !f.isDeleted &&
          !isInvalidExtraFile(f) &&
          !isDeletedKey(f.type, f.asset_file_path || f.file_name || '')
      )
      .map((f) => {
        const isSignerDocument = ['013', '014', '015'].includes(f.type);
        if (isSignerDocument) {
          let dropdownItem = dropdownItems.find(
            (item) => item.id === f.type && item.label === f.label
          );
          
          let extractedCuit: string | null = null;
          let extractedSignerTypeCode: string | null = null;
          
          if (!dropdownItem && f.asset_file_path) {
            const filename = f.asset_file_path.split('/').pop() || f.asset_file_path;
            const parts = filename.split('-');
            const cuits: string[] = [];
            
            for (let i = 0; i < parts.length; i++) {
              const part = parts[i];
              if (part && /^\d{11}$/.test(part)) {
                cuits.push(part);
                if (cuits.length >= 2 && i + 1 < parts.length && /^\d{3}$/.test(parts[i + 1])) {
                  extractedSignerTypeCode = parts[i + 1];
                } else if (cuits.length === 1 && i + 1 < parts.length && /^\d{3}$/.test(parts[i + 1])) {
                  extractedSignerTypeCode = parts[i + 1];
                }
              }
            }
            
            if (cuits.length > 0 && extractedSignerTypeCode) {
              extractedCuit = cuits.length >= 2 ? cuits[1] : cuits[0];
              dropdownItem = dropdownItems.find(
                (item) =>
                  item.id === f.type &&
                  !item.isGeneral &&
                  item.cuit === extractedCuit &&
                  item.signer_type_code === extractedSignerTypeCode
              );
            }
          }
          
          if (dropdownItem && dropdownItem.cuit) {
            return {
              file_type: f.type,
              asset_file_path: f.asset_file_path,
              signer_cuit: dropdownItem.cuit,
              signer_type_code: dropdownItem.signer_type_code,
            };
          } else if (extractedCuit && extractedSignerTypeCode) {
            return {
              file_type: f.type,
              asset_file_path: f.asset_file_path,
              signer_cuit: extractedCuit,
              signer_type_code: extractedSignerTypeCode,
            };
          }
          return {
            file_type: f.type,
            asset_file_path: f.asset_file_path,
          };
        }
        return {
          file_type: f.type,
          asset_file_path: f.asset_file_path,
        };
      })
      .filter((f) => f !== undefined) as Array<{ file_type: string; asset_file_path: string; signer_cuit?: string; signer_type_code?: string }>;
    

    // Priorizar archivos actualizados (extra) sobre archivos antiguos (baseFiles)
    const combined = [...extra, ...baseFiles];
    const seen = new Set<string>();
    const unique = combined.filter((file) => {
      const path = file.asset_file_path || '';
      const filename = path.split('/').pop() || path;

      if (['016', '017', '018', '019'].includes(file.file_type)) {
        let core = filename;

        const typeTag = `${file.file_type}-`;
        const typeIndex = filename.indexOf(typeTag);
        if (typeIndex >= 0) {
          core = filename.substring(typeIndex + typeTag.length);
        }

        const timestampRegex =
          /(.*)-\d{2}-\d{2}-\d{4}T\d{2}-\d{2}-\d{2}(\.[^.]*)?$/;
        const match = core.match(timestampRegex);
        if (match) {
          core = `${match[1]}${match[2] || ''}`;
        }

        const key = `${file.file_type}-${core}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      }

      const key = `${file.file_type}-${filename}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    

    const totalArs = (clientData.products || []).reduce(
      (acc, p: any) => acc + (p.amount?.value || 0),
      0
    );

    const adjustedRequestedAmount =
      (clientData.requested_amount && clientData.requested_amount.some((ra: any) => ra.value > 0))
        ? clientData.requested_amount
        : [
            { currency_code: 'ARS', value: totalArs },
            { currency_code: 'USD', value: 0 },
          ];

    const adjustedNoteAmount =
      (noteAmount && noteAmount > 0) ||
      (clientData.note_amount && clientData.note_amount > 0)
        ? noteAmount || clientData.note_amount || 0
        : totalArs;

    const adjustedGuaranteeAmount =
      (guaranteeAmount && guaranteeAmount > 0) ||
      (clientData.guarantee_amount && clientData.guarantee_amount > 0)
        ? guaranteeAmount || clientData.guarantee_amount || 0
        : totalArs;

    setNoteAmount(adjustedNoteAmount);
    setGuaranteeAmount(adjustedGuaranteeAmount);

    const hydratedFromSave: LocalDocument[] = unique
      .filter((appFile) => ['013', '014', '015', '016', '017', '018', '019'].includes(appFile.file_type))
      .map((appFile, idx) => {
        const baseName = getCleanFileName(
          appFile.file_type,
          appFile.asset_file_path || ''
        );
        const labelFromDropdown =
          findDropdownItemForDoc(
            { ...appFile, type: appFile.file_type },
            dropdownItems
          )?.label || '';
        return {
          id: appFile.asset_file_path || generateId(),
          type: appFile.file_type,
          label: labelFromDropdown,
          asset_file_path: appFile.asset_file_path,
          file_name: baseName,
          isPending: false,
          isDeleted: false,
          order: idx,
        } as LocalDocument;
      });

    const updatedClientData = {
      ...clientData,
      signers: clientData.signers,
      application_files: unique,
      requested_amount: adjustedRequestedAmount,
      note_amount: adjustedNoteAmount,
      guarantee_amount: adjustedGuaranteeAmount,
    };

    console.log('[save] updatedExtraFiles', updatedExtraFiles.map((f) => ({
      id: f.id,
      type: f.type,
      asset_file_path: f.asset_file_path,
      isPending: f.isPending,
      isDeleted: f.isDeleted,
    })));
    console.log('[save] unique application_files', unique);
    
    setExtraFiles(() =>
      reconcileDocuments({
        base: hydratedFromSave,
        locals: [],
      })
    );
    
    setClientData(updatedClientData);

    const normalizedSigners = clientData.signers.map((signer) => ({
      name: signer.name || '',
      cuit: signer.cuit || '',
      locality: signer.locality || '',
      address: signer.address || '',
      role: signer.role || '',
      email: signer.email || '',
      signer_type_code: signer.signer_type_code || ['001'],
      asset_file_path: signer.asset_file_path || '',
    }));


    onSave(
      {
        clientInfo: updatedClientData,
        application_files: unique,
        signers: normalizedSigners,
        corporateStructure: clientData.corporate_structure,
        note_amount: adjustedNoteAmount,
        guarantee_amount: adjustedGuaranteeAmount,
      },
      shouldContinue
    );

    setHasUnsavedChanges(false);
    skipNextExtraFilesChange.current = true;
  };

  const renderLegalAddress = () => {
    return (
      <Card margin={{ top: '4' }}>
        <Card
          variant={clientTheme ? 'primary' : 'success'}
          modifier='weak'
          spacing={'2'}
          display='flex'
          justifyContent='space-between'
        >
          <H
            type='3'
            variant='neutral-1'
            withMargin={false}
            weight='bold'
            modifier='strong'
          >
            Domicilio Legal
          </H>
        </Card>
        <Card display='flex' margin={{ top: '3', bottom: '4' }} gap='24px'>
          <Card width='30vw'>
            <Input
              label='Domicilio legal'
              id='instrumentation-input-legal_company_address'
              value={clientData.basic_data.legal_company_address || ''}
              maxLength={500}
              onChange={(e) =>
                setClientData({
                  ...clientData,
                  basic_data: {
                    ...clientData.basic_data,
                    legal_company_address: e.target.value,
                  },
                })
              }
            />
          </Card>
          <Card width='30vw'>
            <DropDown
              id='instrumentation-legal_state-dropdown'
              label='Provincia legal'
              items={provinces}
              size='md'
              title={clientData.basic_data.legal_state || ''}
              onChange={(e) =>
                setClientData({
                  ...clientData,
                  basic_data: {
                    ...clientData.basic_data,
                    legal_state: provinces.find((item) => item.label === e)?.label || '',
                  },
                })
              }
            ></DropDown>
          </Card>
          <Card width='30vw'>
            <Input
              label='Localidad legal'
              id='instrumentation-input-legal_city'
              value={clientData.basic_data.legal_city || ''}
              maxLength={300}
              onChange={(e) =>
                setClientData({
                  ...clientData,
                  basic_data: {
                    ...clientData.basic_data,
                    legal_city: e.target.value,
                  },
                })
              }
            />
          </Card>
        </Card>
      </Card>
    );
  };

  return (
    <>
      {renderBasicData(clientData)}
      {renderLegalAddress()}
      {renderSignersList()}
      {renderApplicationSummary()}
      <Card
        variant={clientTheme ? 'primary' : 'success'}
        modifier='weak'
        spacing={'2'}
        margin={{ top: '4' }}
      >
        <Card display='flex' justifyContent='space-between' alignItems='center' width='100%'>
          <H
            type='4'
            variant='neutral-1'
            withMargin={false}
            weight='bold'
            modifier='strong'
          >
            Documentación
          </H>
          <Card>
            {filteredFiles && filteredFiles.length === 0 ? (
              <Tooltip
                position='top'
                showArrow={true}
                msg='No hay archivos disponibles'
              >
                <Button
                  variant='primary'
                  size='md'
                  icon='download_outline'
                  iconAlign='left'
                  onClick={handleDownloadAll}
                  disabled={true}
                >
                  Descargar todos
                </Button>
              </Tooltip>
            ) : (
              <Button
                variant='primary'
                size='md'
                icon='download_outline'
                iconAlign='left'
                onClick={handleDownloadAll}
                disabled={bulkDownloading}
              >
                {bulkDownloading ? 'Creando ZIP...' : 'Descargar todos'}
              </Button>
            )}
          </Card>
        </Card>
      </Card>
      {renderDocuments()}
      {extraFiles.filter((f) => !isInvalidExtraFile(f)).length > 0 &&
        extraFiles
          .filter((f) => !isInvalidExtraFile(f))
          .map((file, index) => (
          <Card key={`extra-file-${file.id}`}>
            <Card margin={{ top: '3' }}>
              <FileRowContainer>
                <Icon variant='neutral' name='document_outline' />
                <DropdownWrapper>
                  <DropDown
                    key={`dropdown-${file.id}-${file.label}`}
                    id={`dropdown-${file.id}`}
                    items={generateDataForDropdown(clientData.signers).map((item) => ({
                      label: item.label,
                      disabled: false,
                    }))}
                    size='md'
                    title={file.label || 'Seleccioná el tipo de documento'}
                    onChange={(label) => handleDropdownChange(label, file.id)}
                  />
                </DropdownWrapper>
                <FileNameWrapper>
                  <Card
                    display='flex'
                    justifyContent='space-between'
                    width='100%'
                    variant='neutral'
                    modifier='inverted'
                    height='46px'
                    alignItems='center'
                    spacing={{ left: '2', right: '2' }}
                    borderRadiusAmount='3'
                    border
                    contain='content'
                  >
                    <Label variant='neutral' modifier='strong' type='2'>
                      {file.file_name}
                    </Label>
                    <Card display='flex' gap='10px'>
                      <Icon
                        name='view_outline'
                        variant='neutral'
                        height={44}
                        cursor='pointer'
                        onClick={() => {
                          const path = file.asset_file_path || file.file_name || '';
                          handleExtraFileDownload(path, file.type);
                        }}
                      />
                      <Icon
                        name='delete_outline'
                        variant='neutral'
                        height={44}
                        cursor='pointer'
                        onClick={() => {
                          handleExtraFileDelete(file, file.id);
                        }}
                      />
                    </Card>
                  </Card>
                </FileNameWrapper>
              </FileRowContainer>
            </Card>
            {extraFilesErrors[index] && (
              <Card margin={{ top: '1', left: '6' }}>
                <Span variant='danger' type='2'>
                  {extraFilesErrors[index]}
                </Span>
              </Card>
            )}
          </Card>
        ))}
      {extraFiles.filter((f) => !isInvalidExtraFile(f)).length > 0 && (
        <Card margin={{ top: '5' }} />
      )}
      <BulkDocumentUploader
        documentTypes={getDocumentTypesForBulkUploader()}
        onFilesUploaded={handleBulkFilesUploaded}
        onValidationChange={handleBulkValidationChange}
        maxFileSize={MAX_FILE_SIZE}
        acceptedFormats={ACCEPTED_FORMATS}
        disabled={isSaving || !cuit}
        cuit={cuit}
        onFileUpload={handleBulkFileUpload}
      />
      <Card display='flex' justifyContent='flex-end' margin={{ top: '5' }}>
        <Card display='flex' justifyContent='flex-end' gap='16px' width='420px'>
          <Button
            id='client-button-save'
            size='lg'
            variant='secondary'
            onClick={() => handleSave(false)}
            disabled={!hasUnsavedChanges || isSaving || clientData.signers.length === 0 || hasErrors || hasFilesWithoutLabel}
            fullWidth
            style={
              !hasUnsavedChanges && !isSaving && clientData.signers.length > 0 && !hasErrors && !hasFilesWithoutLabel
                ? {
                    backgroundColor: '#E5E5E5',
                    color: '#000000',
                    boxShadow: 'none',
                    cursor: 'not-allowed',
                  }
                : undefined
            }
          >
            {isSaving ? <Spinner size='sm' variant='primary' /> : 'Guardar'}
          </Button>
          <Button
            id='client-button-continue'
            size='lg'
            variant='primary'
            onClick={() => handleSave(true)}
            fullWidth
            disabled={isSaving || clientData.signers.length === 0 || hasErrors || hasFilesWithoutLabel}
          >
            {isSaving ? <Spinner size='sm' variant='primary' /> : 'Continuar'}
          </Button>
        </Card>
      </Card>
    </>
  );
};

export default Resume;
